import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight} from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import StoreStyle from '../../assets/stylesheets/store/StorePage.module.css';
import PopularItems from '../../components/store/categories/PopularItemsCategory';
import SeasonalItems from '../../components/store/categories/SeasonalItemsCategory';
import DealsOfWeek from '../../components/store/categories/DealsOfWeekCategory';
import NewThisWeek from '../../components/store/categories/NewItemsCategory';
import FarmSpotlight from '../../components/store/categories/FarmSpotlightCategory';

import Bakery    from "../../assets/img/sample/Bakery.jpg";
import Market    from "../../assets/img/sample/Market.jpg";
import Meats    from "../../assets/img/sample/Meats.jpg";
import Pumpkin    from "../../assets/img/sample/pumpkin.jpg";

import Season    from "../../assets/img/sample/Season.jpg";
import Fruits    from "../../assets/img/sample/Fruits.jpg";
import Vegetables    from "../../assets/img/sample/Vegetables.jpg";
import Flowers    from "../../assets/img/sample/Flowers.jpg";
import MeatSeafood    from "../../assets/img/sample/Meat-Seafood.jpg";
import DairyEggs    from "../../assets/img/sample/Dairy-Eggs.jpg";
import Frozen    from "../../assets/img/sample/Frozen.jpg";
import SyrupsHoney    from "../../assets/img/sample/Syrups-Honey.jpg";
import Wine    from "../../assets/img/sample/Wine.jpg";
import Pantry    from "../../assets/img/sample/Pantry.jpg";
import ProduceBoxes    from "../../assets/img/sample/Produce-Boxes.jpg";

const CATEGORIES = [
  { title: 'New & Season', slug: 'new-peak-season', img:Season },
  { title: 'Fruits', slug: 'fruits', img:Fruits},
  { title: 'Vegetables', slug: 'vegetables', img:Vegetables },
  { title: 'Flowers', slug: 'flowers', img: Flowers },
  { title: 'Meat & Seafood', slug: 'meat-seafood', img:MeatSeafood },
  { title: 'Dairy & Eggs', slug: 'dairy-eggs', img:DairyEggs },
  { title: 'Bakery', slug: 'bakery', img:Bakery },
  { title: 'Frozen', slug: 'frozen', img:Frozen },
  { title: 'Syrups & Honey', slug: 'syrups-honey', img: SyrupsHoney },
  { title: 'Wine & Cider', slug: 'wine-cider', img: Wine },
  { title: 'Pantry', slug: 'pantry', img:Pantry },
  { title: 'Produce Boxes', slug: 'produce-boxes', img: ProduceBoxes }
];

const BANNERS = [
  {
    id: 'farm-market',
    title: 'Farm Market',
    desc: 'Freshly picked, curated by season',
    img: Market,
    to: '/*'
  },
  {
    id: 'local-meat',
    title: 'Grass-fed Meats',
    desc: 'From trusted local producers',
    img: Meats,
    to: '/*'
  },
  {
    id: 'bakery-treats',
    title: 'Artisan Bakery',
    desc: 'Fresh breads and pastries daily',
    img: Bakery,
    to: '/*'
  },
  {
    id: 'pumpkin-festival',
    title: 'Pumpkin',
    desc: 'Handpicked deals this week',
    img: Pumpkin,
    to: '/*'
  }
];

const PROMOS = [
  { id: 'syrup', title: 'Local Maple Syrup', img: SyrupsHoney, to: '/*' },
  { id: 'meat', title: 'Grass-fed Beef Picks', img: Meats, to: '/*' },
  { id: 'bakery', title: 'Fresh Bakery Daily', img: Bakery, to: '/*' },
];

export default function FarmStorePage() {
  // Banner carousel state/effect
  const [bannerIndex, setBannerIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setBannerIndex((i) => (i + 1) % BANNERS.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);
  const bannerSlideWidthPct = 100 / BANNERS.length;

  // Swiper navigation button refs (fix CSS Modules selector issues)
  const prevRef = React.useRef(null);
  const nextRef = React.useRef(null);

  return (
    <div className={StoreStyle['store-page']}>
      {/* Container */}
      <div className={StoreStyle['store-container']}>
        {/* Farm Store is under active development. We’re building something exciting. Stay tuned! */}
        <div className={`${StoreStyle.notice} ${StoreStyle['notice--blue']}`} role="note">
          <span aria-hidden="true" className={StoreStyle['notice-emoji']}>ℹ️</span>
          <p className={StoreStyle['notice-text']}>
            Farm Store is under active development. We’re building something exciting. The page you’re viewing is a mockup and is not yet functional.
          </p>
        </div>

        {/* Hero */}
        <section className={StoreStyle.hero}>
          {/* Search stub (UI only) */}
          <div className={StoreStyle['search-row']}>
            <div className={StoreStyle['search-form']}>
              <input
                type="text"
                placeholder="Search products, farms, or categories"
                className={StoreStyle['search-input']}
              />
              <button className={StoreStyle['search-button']}>Search</button>
            </div>
          </div>
        </section>

   {/* Categories - horizontal chips with arrows */}
        <section className={StoreStyle['categories-section']}>
          <div className={StoreStyle['categories-row']}>

            <div className={StoreStyle['categories-scroll']}>
              <Swiper
                modules={[Navigation]}
                spaceBetween={8}
                navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                onBeforeInit={(swiper) => {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                }}
                className={StoreStyle['category-swiper']}
                breakpoints={{
                  0: { slidesPerView: 5 },
                  768: { slidesPerView: 7 }
                }}
              >
                {CATEGORIES.map((cat) => (
                  <SwiperSlide key={cat.slug}>
                    <Link to={`/store/list/category/${cat.slug}`} className={StoreStyle['category-chip']}>
                      {cat.title}
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Navigation Arrows */}
              <button
                ref={prevRef}
                className={`${StoreStyle['store-nav-arrow']} ${StoreStyle['store-swiper-button-prev']}`}
                aria-label="Previous"
              >
                <ChevronLeft  />
              </button>
              <button
                ref={nextRef}
                className={`${StoreStyle['store-nav-arrow']} ${StoreStyle['store-swiper-button-next']}`}
                aria-label="Next"
              >
                <ChevronRight />
              </button>

            </div>
          </div>
        </section>


        {/* Banner carousel */}
        <section className={StoreStyle['banner-section']}>
          <div className={StoreStyle['banner-wrapper']}>
            <div
              className={StoreStyle['banner-track']}
              style={{ width: `${BANNERS.length * 100}%`, transform: `translateX(-${bannerIndex * bannerSlideWidthPct}%)` }}
            >
              {BANNERS.map((b) => (
                <Link key={b.id} to={b.to} style={{ width: `${bannerSlideWidthPct}%`, textDecoration: 'none', color: 'inherit' }}>
                  <div className={StoreStyle['banner-slide']} style={{ backgroundImage: `url(${b.img})` }}>
                    <div className={StoreStyle['banner-gradient']} />
                    <div className={StoreStyle['banner-caption']}>
                      <div className={StoreStyle['banner-title']}>{b.title}</div>
                      <div className={StoreStyle['banner-desc']}>{b.desc}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {/* Dots */}
            <div className={StoreStyle['banner-dots']}>
              {BANNERS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setBannerIndex(i)}
                  aria-label={`Go to banner ${i + 1}`}
                  className={`${StoreStyle['banner-dot']} ${i === bannerIndex ? StoreStyle['active'] : ''}`}
                />
              ))}
            </div>
          </div>
        </section>

     
        {/* Deals items */}
        <DealsOfWeek title="This Week’s Deals" to={`/store/list/topic/deals`} />
        {/* New items */}
        <NewThisWeek title="New This Week" to={`/store/list/topic/new`} />

        {/* Farm items */}
        <FarmSpotlight title="From Mockridge Farm" to={`/*`} farmName="Mockridge Farm" />

        {/* Popular items */}
        <PopularItems title="Popular items" to={`/store/list/topic/popular`} />


        {/* Seasonal picks */}
        <SeasonalItems title="Seasonal picks" to={`/store/list/topic/seasonal`} />
        
        {/* Small sponsored promos - unobtrusive */}
        <section className={`${StoreStyle['promo-section']} ${StoreStyle['promo-panel']}`}>
          <div className={StoreStyle['promo-inner']}>
            <div className={StoreStyle['promo-grid']}>
              {PROMOS.map(p => (
                <Link key={p.id} to={p.to} style={{ textDecoration: 'none', color: 'inherit' }} aria-label={`${p.title} (Sponsored)`}>
                  <div className={StoreStyle['promo-card']} style={{ backgroundImage: `url(${p.img})` }}>
                    <span className={StoreStyle['promo-badge']}>Sponsored</span>
                    <div className={StoreStyle['promo-gradient']} />
                    <div className={StoreStyle['promo-title']}>{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className={StoreStyle['cta-section']}>
          <div className={StoreStyle['cta-banner']}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Showcase your farm store</div>
              <div style={{ color: '#16a34a', marginTop: 6 }}>Join the FarmCiti marketplace</div>
            </div>
            <Link to="mailto:farmciticanada@gmail.com" className={StoreStyle['cta-button']}>Contact us</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
